/* eslint-disable complexity */
import { useAppConfig } from "~/app/config/useAppConfig";
import { useEffect, useMemo } from "react";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { selectDeliveryModeDetails, selectIsSending } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { useDispatch, useSelector } from "react-redux";
import { userIsInquirer, userIsInternalCustomer, userIsLoggedIn } from "~/domains/user/utils/roles";
import { type RootState } from "~/app/store";
import { PackageEntrySource } from "~/features/cart/cart/models/CartData";
import Modal from "~/common/components/Modal";
import { useBooleanState } from "~/common/hooks/useBooleanState";

import { OrderSummary } from "~/features/cart/cartV2/OrderSummary";
import { CheckoutFormSaver, useCheckoutLogic } from "~/features/cart/cart/useCheckoutLogic";
import { FormProvider } from "react-hook-form";
import { type DeliveryOptionItem } from "./DeliveryOption";
import { Box, Button, Stack } from "~/common/mui";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";
import { trackCheckoutLoadThunk } from "~/features/cart/cart/redux/actions/checkoutPageActions";
import { useTranslation } from "react-i18next";
import { openModal } from "~/common/utils/ModalService";
import { CantQuoteSalesCampaignModal } from "~/features/salesCampaign/components/dialogs/CantQuoteSalesCampaignModal/CantQuoteSalesCampaignModal";
import { ErrorCode } from "~/features/cart/cart/api/placeOrder";
import { SoldOutCampaignModal } from "~/features/salesCampaign/components/dialogs/SoldOutCampaignModal/SoldOutCampaignModal";
import ProductTableV2 from "~/features/cart/cartV2/ProductTable";
import { SendingType } from "~/features/cart/cart/redux/types";
import CheckoutSummary from "./CheckoutSummary/CheckoutSummary";
import { CartSectionTitle } from "./CartSectionTitle";
import { useCheckoutCartViewModel } from "~/features/cart/checkout/hooks/useCheckoutCartViewModel";
import { useQuery } from "@tanstack/react-query";
import { checkoutSummaryPageQueryOptions } from "~/app/api/pageData";
import { cartOrderContextQueryOptions } from "~/domains/cart/api/cartOrderContext";
import { useSession } from "~/app/providers/SessionProvider";
import { activeCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";

export interface CheckoutForm {
  poNumber: string;
  orderComment: string;
  deliveryCode: string;
  completeDelivery: DeliveryOptionItem;

  optime?: CheckoutFormOptime;
  paymentType: string;
  creditCardID: string;
  freightPaymentCode: string;
  freightPaymentAccountNumber: string;
  orderType: string;
}

export interface CheckoutFormOptime {
  consent: boolean;
  email: string;
  name: string;
  lastName: string;
  plant: string;
  timeZone: string;
  language: string;
  country: string;
}

export interface FreightPaymentItem {
  type: string;
  accountNumber: boolean;
}

const TOAST_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.Generic]: "checkout.placeOrder.failed",
  [ErrorCode.CreditCard]: "checkout.placeOrder.payment.failed",
  [ErrorCode.SaleStock]: "",
};

const CheckoutCart = () => {
  const { t } = useTranslation();
  const session = useSession();
  const activeCartId = activeCartResourceId(session?.activeCart, userIsLoggedIn());
  const { data: pageData } = useQuery(checkoutSummaryPageQueryOptions(activeCartId));
  const translationCountryCode = pageData?.status === "OK" ? pageData.data.translationCountryCode ?? "" : "";
  const { data: cartOrderContextData } = useQuery(cartOrderContextQueryOptions());
  const selectedOrderType = cartOrderContextData?.selectedOrderType ?? "";

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;

  const { any: hasSomeSalesCampaignItems, all: hasOnlySalesCampaignItems } = useSelector(
    selectContainsSalesCampaignItems,
  );

  const {
    totalWeight,
    totalSum,
    totalDiscount,
    hasAnySurcharge,
    hasAnyTariffSurcharge,
    surchargeValue,
    orderablesViewModel,
    bundleViewModels,
    quotablesViewModel,
  } = useCheckoutCartViewModel();

  const containsQuoteItemsInQuotables = useSelector(selectContainsQuoteInQuotables);
  const containsQuoteItemsAtAll = useSelector(selectContainsQuoteItemsAtAll);

  const { handleSubmitClick, formProps, placeOrderError, resetPlaceOrderError } = useCheckoutLogic();

  const { watch, setValue } = formProps;

  const deliveryCode = watch("deliveryCode");
  const methodPayment = watch("paymentType");
  const orderTypeState = watch("orderType");
  const deliveryDetails = useSelector(selectDeliveryModeDetails(deliveryCode, methodPayment));
  const expressDelivery = "EXPRESS";

  let extraElements = undefined;
  if (deliveryDetails && deliveryDetails?.paymentCodeDisplay) {
    const shipmentMethodLabel = t(`data.shipmentMethod.${deliveryDetails.code}`);

    extraElements = [
      {
        label: t("checkout.shipmentInfo.label", {
          context: translationCountryCode,
          shipmentType: shipmentMethodLabel,
        }),
        value:
          deliveryDetails.code === expressDelivery
            ? t(`data.paymentCodeDisplay.EXPRESS`, {
                context: translationCountryCode,
              })
            : undefined,
      },
    ];
  }

  const hasCartElement =
    orderablesViewModel.rows.some((r) => r.kind === "entry") ||
    bundleViewModels.some((vm) => vm.rows.some((r) => r.kind === "entry"));
  const hasQuoteElement = quotablesViewModel.rows.some((r) => r.kind === "entry");

  const displayOrderableItemsSection = orderablesViewModel.rows.some((r) => r.kind === "entry");

  const sending = useSelector(selectIsSending);

  const {
    val: showQuoteItemsFromQuoteInfo,
    setTrue: openShowQuoteItemsFromQuoteInfo,
    setFalse: closeShowQuoteItemsFromQuoteInfo,
  } = useBooleanState(false);

  const dispatch = useDispatch();

  const disablePlaceQuote =
    webservicesDisabled ||
    !userHasPermission(Permission.PLACE_QUOTE) ||
    hasOnlySalesCampaignItems ||
    sending === SendingType.ORDER;
  const disablePlaceOrder =
    webservicesDisabled ||
    !userHasPermission(Permission.PLACE_ORDER) ||
    userIsInquirer() ||
    userIsInternalCustomer() ||
    sending === SendingType.QUOTE;

  const bothButtonsDisabled = disablePlaceOrder && disablePlaceQuote;

  const actionButtons = useMemo(() => {
    const mixedCart = true;

    const actions = [];

    if (hasCartElement) {
      actions.push(
        <Button
          key={"placeOrder"}
          data-meta={"checkout"}
          data-test-id={hasQuoteElement ? "btn-place-mixed" : "btn-place-order"}
          className={"f-no-wrap"}
          fullWidth={true}
          size="large"
          disabled={disablePlaceOrder}
          loading={sending === SendingType.ORDER}
          onClickCapture={() => {
            const paymentType = formProps.getValues("paymentType");
            dispatch(trackCheckoutLoadThunk(paymentType));
          }}
          onClick={() => {
            if (containsQuoteItemsInQuotables) {
              openShowQuoteItemsFromQuoteInfo();
            } else {
              handleSubmitClick(false);
            }
          }}
        >
          {hasQuoteElement ? t("page.checkout.btn.mixedOrder") : t("checkoutPage.btn.order")}
        </Button>,
      );
    }

    actions.push(
      <Button
        key={"placeQuote"}
        data-meta={"checkout"}
        data-test-id={"btn-place-quote"}
        color={hasCartElement ? "secondary" : "primary"}
        className={"f-no-wrap"}
        fullWidth={true}
        size="large"
        disabled={disablePlaceQuote}
        loading={sending === SendingType.QUOTE}
        onClickCapture={() => {
          const paymentType = formProps.getValues("paymentType");
          dispatch(trackCheckoutLoadThunk(paymentType));
        }}
        onClick={() => {
          if (containsQuoteItemsAtAll) {
            openShowQuoteItemsFromQuoteInfo();
          } else if (hasSomeSalesCampaignItems) {
            openModal("CantQuoteSalesCampaignModal", (onClose) => (
              <CantQuoteSalesCampaignModal
                onClose={onClose}
                disablePlaceOrder={disablePlaceOrder}
                onPlaceOrder={() => handleSubmitClick(false)}
              />
            ));
          } else {
            handleSubmitClick(true);
          }
        }}
      >
        {mixedCart ? t("page.checkout.btnDescription.quote") : t("checkoutPage.btn.quote")}
      </Button>,
    );

    return actions;
  }, [sending, t, handleSubmitClick, hasQuoteElement, hasCartElement]);

  const packageEntriesById = useSelector((state: RootState) => state.cart.packageEntriesById);

  // create a list of all available products in checkout
  // extract for each product user's delivery date
  const cartProductsDeliveryDateList: Date[] = Object.values(packageEntriesById)
    .filter((packData) => packData.edd && packData.edd.date !== null && packData.edd.date !== undefined)
    .map((packData) => packData.edd!.date) as Date[];

  useEffect(() => {
    if (!orderTypeState && !!selectedOrderType) {
      setValue("orderType", selectedOrderType);
    }
  }, [selectedOrderType]);

  const showToast = placeOrderError && [ErrorCode.Generic, ErrorCode.CreditCard].includes(placeOrderError);

  useEffect(() => {
    if (placeOrderError === ErrorCode.SaleStock) {
      openModal("OutOfStockModal", (onClose) => (
        <SoldOutCampaignModal
          onClose={() => {
            onClose();
            resetPlaceOrderError();
          }}
        />
      ));
    }
  }, [placeOrderError]);

  return (
    <>
      {showToast && <ToastMessage type={"error"}>{t(TOAST_MESSAGES[placeOrderError])}</ToastMessage>}
      <FormProvider {...formProps}>
        <CheckoutFormSaver />
        <CheckoutSummary
          actionButtons={actionButtons}
          cartProductsDeliveryDateList={cartProductsDeliveryDateList}
          surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
          tariffSurcharge={hasAnyTariffSurcharge}
          bothButtonsDisabled={bothButtonsDisabled}
          totalDiscount={totalDiscount}
        />

        <Stack gap={3}>
          {displayOrderableItemsSection && (
            <>
              <CartSectionTitle
                title={t("cartPage.header.toBeDelivered", {
                  count: orderablesViewModel.rows.filter((r) => r.kind === "entry").length,
                })}
                editCart
              />
              <ProductTableV2 data-test-id={"checkout-section-orderable"} viewModel={orderablesViewModel} />
            </>
          )}

          {bundleViewModels.map((bundleViewModel) => (
            <>
              <CartSectionTitle
                title={t("page.cart.bundledQuote.title", {
                  amount: bundleViewModel.rows.filter((r) => r.kind === "entry").length,
                  quoteNumber: bundleViewModel.quoteNumber,
                })}
                subtitle={t("page.cart.bundledQuote.subtitle")}
                editCart
              />
              <ProductTableV2
                data-test-id={`checkout-section-bundle-${bundleViewModel.quoteNumber}`}
                viewModel={bundleViewModel}
              />
            </>
          ))}

          {hasQuoteElement && (
            <>
              <CartSectionTitle
                title={t("cartPage.header.toBeQuoted", {
                  count: quotablesViewModel.rows.filter((r) => r.kind === "entry").length,
                })}
                editCart
              />
              <ProductTableV2 data-test-id={"checkout-section-quotable"} viewModel={quotablesViewModel} />
            </>
          )}
        </Stack>

        <Box sx={{ display: "flex", flexFlow: "column", alignItems: "flex-end", my: 4 }}>
          <OrderSummary sx={{ justifySelf: "flex-end" }}>
            <OrderSummary.DataContainer>
              <OrderSummary.WeightRow weight={totalWeight} />
              <OrderSummary.SurchargeRow
                surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
              />
              <OrderSummary.DiscountRow discount={totalDiscount} />
              <OrderSummary.TotalRow
                value={webservicesDisabled ? null : totalSum}
                showPriceOnRequest
                asterisk={hasAnySurcharge || hasAnyTariffSurcharge}
              />
              <OrderSummary.TaxDisclaimer />
              {extraElements &&
                extraElements.map((row, i) => <OrderSummary.Row key={i} label={row.label} value={row.value} />)}
            </OrderSummary.DataContainer>
            <OrderSummary.Divider />
            <OrderSummary.Actions>{actionButtons}</OrderSummary.Actions>
          </OrderSummary>
        </Box>

        {showQuoteItemsFromQuoteInfo && (
          <Modal onClose={closeShowQuoteItemsFromQuoteInfo}>{t("page.checkout.message.alreadyQuotedItems")}</Modal>
        )}
      </FormProvider>
    </>
  );
};

export default CheckoutCart;

const selectContainsQuoteInQuotables = (state: RootState) => {
  const quotableGroup = state.cart.entryGroups.find((group) => group.groupType === "QUOTABLE");
  if (!quotableGroup || !quotableGroup.children.length) {
    return false;
  }
  const quotableGroupNumber = quotableGroup.groupNumber;

  const packageEntriesById = state.cart.packageEntriesById;
  return Object.values(packageEntriesById).some(
    (packEntry) =>
      packEntry.source === PackageEntrySource.QUOTE && packEntry.groupNumbers.includes(quotableGroupNumber),
  );
};

const selectContainsQuoteItemsAtAll = (state: RootState) => {
  return Object.values(state.cart.packageEntriesById).some(
    (packEntry) => packEntry.source === PackageEntrySource.QUOTE,
  );
};

const selectContainsSalesCampaignItems = (state: RootState) => {
  let any = false;
  let all = true;
  Object.values(state.cart.packageEntriesById).forEach((packEntry) => {
    any = any || !!packEntry.saleCampaignEnabled;
    all = all && !!packEntry.saleCampaignEnabled;
  });
  return {
    any,
    all,
  };
};
